import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import highchartsMore from "highcharts/highcharts-more";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ClimateHazardsPeriod, ClimateHazardsScenario} from "../../../../../types/api/assets";
import {CLIMATE_HAZARDS_SERIES_COLORS, ClimateHazardRow} from "./climateHazardsEvolution";

highchartsMore(Highcharts);

export default function ClimateHazardsRadarChart({
                                                   rows,
                                                   period,
                                                   getHazardLabel,
                                                   getClimateVariableLabel,
                                                   scaleRange,
                                                   title,
                                                   height = 420
                                                 }: {
  rows: ClimateHazardRow[],
  period: ClimateHazardsPeriod,
  getHazardLabel: (hazardCode: string) => string,
  getClimateVariableLabel: (ccv: string) => string,
  scaleRange: { min: number, max: number, tickPositions: number[] },
  title?: string,
  height?: number,
}) {

  const {t, i18n} = useTranslation()

  const options = useMemo((): Highcharts.Options => {
    const formatValue = (value: number) => value.toLocaleString(i18n.language, {minimumFractionDigits: 2, maximumFractionDigits: 2})
    const notImpacted = rows.filter(row => !row.impacted).map(row => getHazardLabel(row.hazardCode))
    const scenarioSeries = (scenario: ClimateHazardsScenario, name: string, symbol: string): Highcharts.SeriesLineOptions => ({
      type: 'line',
      name,
      color: CLIMATE_HAZARDS_SERIES_COLORS[scenario],
      marker: {symbol, radius: 4},
      data: rows.map(row => ({y: row.periods[period][scenario].value, custom: {ccv: row.periods[period][scenario].ccv}})),
    })
    return {
      chart: {polar: true, type: 'line', height},
      title: title ? {text: title, style: {fontSize: '14px', fontWeight: '600'}} : {text: undefined},
      credits: {enabled: false},
      pane: {size: '72%'},
      xAxis: {
        categories: rows.map(row => getHazardLabel(row.hazardCode)),
        tickmarkPlacement: 'on',
        lineWidth: 0,
        labels: {
          formatter() {
            const label = String(this.value)
            return notImpacted.includes(label) ? `<span style="color:#8b919b;font-style:italic">${label}</span>` : label
          },
          style: {fontSize: '11px'},
        },
      },
      yAxis: {
        gridLineInterpolation: 'polygon',
        min: scaleRange.min,
        max: scaleRange.max,
        tickPositions: scaleRange.tickPositions,
        showFirstLabel: true,
        showLastLabel: true,
        lineWidth: 0,
        labels: {
          // the outer polygon value is highlighted
          formatter() {
            const label = Number(this.value).toLocaleString(i18n.language)
            return this.isLast ? `<b style="color:#52514e">${label}</b>` : label
          },
          style: {fontSize: '10px', color: '#8b919b'},
        },
      },
      legend: {align: 'center', verticalAlign: 'bottom'},
      tooltip: {
        shared: true,
        formatter() {
          const row = rows[this.points?.[0]?.point.index ?? 0]
          const header = `<b>${getHazardLabel(row.hazardCode)}</b>`
          if (!row.impacted) {
            return `${header}<br/>${t('climateChange.notImpacted')}: <b>${formatValue(row.t0)}</b>`
          }
          return [header, ...(this.points || []).map(point => {
            const ccv = (point.point as any).custom?.ccv
            return `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatValue(point.y as number)}</b>` +
              (ccv ? ` <span style="color:#8b919b">(${getClimateVariableLabel(ccv)})</span>` : '')
          })].join('<br/>')
        },
      },
      plotOptions: {series: {lineWidth: 2}},
      series: [
        {
          type: 'line',
          name: t('climateChange.current'),
          color: CLIMATE_HAZARDS_SERIES_COLORS.t0,
          dashStyle: 'ShortDash',
          marker: {symbol: 'circle', radius: 4},
          data: rows.map(row => row.t0),
        },
        scenarioSeries('ssp245', 'SSP2 4.5', 'square'),
        scenarioSeries('ssp370', 'SSP3 7.0', 'triangle'),
      ],
    }
  }, [rows, period, getHazardLabel, getClimateVariableLabel, scaleRange, title, height, t, i18n.language])

  return <HighchartsReact highcharts={Highcharts} options={options}/>
}
